import { useState } from "react";
import { format, parse, isValid } from "date-fns";
import { fr } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";

// Emits/receives an ISO string "yyyy-MM-dd" to keep the backend contract unchanged.
export function DateField({ value, onChange, birth = false, placeholder = "Choisir une date", testId }) {
  const [open, setOpen] = useState(false);
  const selected = value ? parse(value, "yyyy-MM-dd", new Date()) : undefined;
  const validSelected = selected && isValid(selected) ? selected : undefined;

  const now = new Date();
  const toYear = now.getFullYear() + 2;
  const fromYear = 1920;
  const defaultMonth = validSelected || (birth ? new Date(1990, 0, 1) : now);
  const disabled = birth ? { after: now } : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={cn(
            "flex h-12 w-full items-center gap-3 rounded-xl border-2 border-slate-200 bg-white px-4 text-left transition-colors hover:border-blue-300 focus:outline-none focus:border-blue-500",
            !validSelected && "text-slate-400"
          )}
        >
          <CalendarIcon size={18} className="text-blue-600 shrink-0" />
          <span className="text-sm font-medium text-slate-800">
            {validSelected ? format(validSelected, "d MMMM yyyy", { locale: fr }) : placeholder}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 rounded-2xl" align="start">
        <Calendar
          mode="single"
          locale={fr}
          selected={validSelected}
          defaultMonth={defaultMonth}
          captionLayout="dropdown-buttons"
          fromYear={fromYear}
          toYear={toYear}
          disabled={disabled}
          onSelect={(d) => {
            if (d) onChange(format(d, "yyyy-MM-dd"));
            setOpen(false);
          }}
          classNames={{
            caption: "flex justify-center pt-1 relative items-center gap-2",
            caption_dropdowns: "flex gap-2",
            dropdown: "rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm font-medium capitalize focus:outline-none focus:ring-2 focus:ring-blue-200",
            dropdown_month: "relative",
            dropdown_year: "relative",
            vhidden: "hidden",
            caption_label: "hidden",
            day_selected: "bg-blue-600 text-white hover:bg-blue-600 hover:text-white focus:bg-blue-600 rounded-full",
            day_today: "bg-indigo-100 text-indigo-900 rounded-full",
            day: "h-9 w-9 p-0 font-normal rounded-full hover:bg-blue-50 aria-selected:opacity-100",
            head_cell: "text-slate-400 w-9 font-semibold text-[0.7rem] uppercase",
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
